import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import WorkflowFooter from '../../Components/workflow/WorkflowFooter';
import { UserDeployInfoWorkflow } from './DeployPackageWorkflow';
import { useTheme } from '@canopysecurity/component-library';
import {
  HeadingText,
  ParagraphText,
  SectionTitleText,
} from '../../StyledComponents/Typography';
import styled from 'styled-components';
import pixelRatio from '@canopysecurity/component-library/utils/pixelRatio';
import { HomeBaseTableRow } from '../../StyledComponents/Table';
import CondorSpinner from '../../Components/CondorSpinner';
import cutecondor from '../../assets/cute-condor.png';
import { useQuery } from '@tanstack/react-query';
import { getTags, Tag } from '../../services/TagsService';
import { getDeviceTags } from '../../services/DevicesService';

const FormLabelContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: ${pixelRatio.heightPercentageToDP('.2')}em 0px
    ${pixelRatio.heightPercentageToDP('0.1')}em;
  justify-content: flex-start;
  align-items: center;
`;

const FormLabel = styled.label`
  font-weight: bold;
  font-size: 16px;
  line-height: 24px;
`;

const TableContainer = styled.div`
  margin-top: 3%;
  padding: 0 5% 0 5%;
  height: 40vh;
  margin-bottom: 4em;
`;

const TableRows = styled.div`
  grid-template-columns: 25fr 25fr 50fr;
  display: grid;
  width: 100%;
  height: 80%;
  overflow-y: auto;
`;

const TagRowContainer = styled(HomeBaseTableRow)`
  grid-template-columns: 1fr 1fr 4fr;
`;

function TagRow({
  tag,
  index,
  onChange,
  isSelected,
}: {
  tag: Tag;
  index: number;
  isSelected: boolean;
  onChange: (tag: Tag) => void;
}) {
  const theme = useTheme();

  const { data: deviceTagsData } = useQuery({
    queryKey: ['deviceTags', tag.tagId],
    queryFn: async () => getDeviceTags({ tagId: tag.tagId! }),
    enabled: !!tag.tagId,
  });

  const deviceTags = useMemo(() => {
    const hasMore = !!deviceTagsData?.next;
    const serialNumbers = (deviceTagsData?.results ?? []).map(
      (dT) => dT?.serialNumber,
    );

    if (serialNumbers.length === 0) {
      return '(0 Devices)';
    }
    if (serialNumbers.length === 1) {
      return `(1 Device) ${serialNumbers[0]}`;
    }
    if (serialNumbers.length > 1 && !hasMore) {
      return `(${serialNumbers.length} Devices) ${serialNumbers.join(', ')}`;
    }
    if (serialNumbers.length > 1 && hasMore) {
      return `(${serialNumbers.length}+ Devices) ${serialNumbers
        .slice(0, 5)
        .join(', ')}...`;
    }
    return '';
  }, [deviceTagsData]);

  return (
    <TagRowContainer
      key={tag.tagId}
      style={{
        backgroundColor:
          index % 2 === 0 ? theme.secondary.darker : theme.background.surface,
      }}
    >
      <div
        style={{
          marginRight: '0.6em',
          justifySelf: 'stretch',
          marginLeft: 'auto',
          maxWidth: 200,
          width: '100%',
        }}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {
            onChange(tag);
          }}
        />
      </div>
      <ParagraphText style={{ alignSelf: 'center' }}>
        {tag.tagName}
      </ParagraphText>
      <ParagraphText style={{ alignSelf: 'center' }}>
        {deviceTags}
      </ParagraphText>
    </TagRowContainer>
  );
}

export const SelectTagsToTarget = ({
  userInput,
  setUserInput,
  goToPreviousStep,
  goToNextStep,
}: {
  userInput: UserDeployInfoWorkflow;
  setUserInput: Dispatch<SetStateAction<UserDeployInfoWorkflow>>;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
}): JSX.Element => {
  const DEPLOY_TAGS_LIMIT = 50;
  const { data, isPending: loading } = useQuery({
    queryKey: ['deploy-tags', DEPLOY_TAGS_LIMIT],
    queryFn: async () => getTags(DEPLOY_TAGS_LIMIT),
  });
  const tags = (data?.results ?? []).filter(Boolean) as Tag[];

  const toggleTag = useCallback(
    (tag: Tag) => {
      const existingTags = userInput.tags.map((t) => t.tagId);
      setUserInput((prev) => ({
        ...userInput,
        tags: existingTags.includes(tag.tagId)
          ? prev.tags.filter((t) => t.tagId !== tag.tagId)
          : [...prev.tags, tag],
      }));
    },
    [userInput, setUserInput],
  );

  const submitValuesRef: React.RefObject<any> = React.createRef();

  const onSubmit = (): void => {
    if (submitValuesRef.current) submitValuesRef.current();
  };

  const clearTags = () => {
    setUserInput((prev) => ({ ...prev, tags: [] }));
  };

  return (
    <div style={{ marginTop: '30px' }}>
      <>
        <FormLabelContainer>
          <FormLabel>Deploy To</FormLabel>
        </FormLabelContainer>

        {loading ? (
          <CondorSpinner />
        ) : (
          <TableContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: '16px',
              }}
            >
              <SectionTitleText>Available Tags to Target</SectionTitleText>
              <div
                style={{
                  marginLeft: 'auto',
                  cursor: 'pointer',
                  paddingRight: '8px',
                }}
                onClick={clearTags}
              >
                <HeadingText>Clear All</HeadingText>
              </div>
            </div>
            {!tags?.length ? (
              <img
                src={cutecondor}
                alt="No tags available to target on"
                style={{ maxHeight: '70%' }}
              />
            ) : (
              <TableRows>
                {tags.map((tag, index) => (
                  <TagRow
                    key={tag.tagId}
                    tag={tag}
                    index={index}
                    onChange={toggleTag}
                    isSelected={
                      !!tag.tagId &&
                      !!userInput.tags?.find((t) => t.tagId === tag.tagId)
                    }
                  />
                ))}
              </TableRows>
            )}
          </TableContainer>
        )}

        <div
          style={{
            marginTop: '24px',
            marginBottom: '24px',
            paddingLeft: '5%',
          }}
        >
          <FormLabelContainer>
            <input
              type="checkbox"
              id="forceInstall"
              checked={userInput.forceInstall}
              onChange={(e) => {
                setUserInput((prev) => ({
                  ...prev,
                  forceInstall: e.target.checked,
                }));
              }}
              style={{
                marginRight: '8px',
                cursor: 'pointer',
                width: '16px',
                height: '16px',
              }}
            />
            <FormLabel htmlFor="forceInstall" style={{ cursor: 'pointer' }}>
              Force Install - Install without waiting for VIM!
            </FormLabel>
          </FormLabelContainer>
        </div>

        <WorkflowFooter
          primaryButton={{
            action: () => {
              onSubmit();
              goToNextStep();
            },
            name: 'Continue',
            disabled: !userInput.tags.length,
          }}
          previousButton={{
            action: () => {
              goToPreviousStep();
            },
            visible: true,
          }}
        />
      </>
    </div>
  );
};
